import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFieldMode } from '../context/FieldModeContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { useToast } from '../hooks/use-toast';
import {
  MobileFormHeader, StepIndicator, TouchButton, AutoSaveIndicator
} from './FieldModeComponents';
import {
  ArrowLeft, ArrowRight, Check, ClipboardList, AlertTriangle,
  MapPin, Users, CheckCircle, Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MobilePTPForm = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isOnline, syncManager, offlineDB } = useFieldMode();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hazardCategories, setHazardCategories] = useState([]);
  
  const [formData, setFormData] = useState({
    // Step 1: Basic Info
    date: new Date().toISOString().split('T')[0],
    project_name: '',
    location: '',
    supervisor: '',
    
    // Step 2: Work Description
    work_description: '',
    equipment_used: '',
    
    // Step 3: Hazards
    selectedHazards: [],
    additional_hazards: '',
    
    // Step 4: Controls
    control_measures: '',
    ppe_required: []
  });

  const ppeOptions = [
    'Hard Hat', 'Safety Glasses', 'Hearing Protection', 'Gloves',
    'Steel Toe Boots', 'High Visibility Vest', 'Face Shield', 'Respirator'
  ];

  // Load hazard categories
  useEffect(() => {
    const fetchHazards = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const response = await fetch(`${API_URL}/api/daily-ptp/templates/hazards`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setHazardCategories(data.hazard_categories || []);
        }
      } catch (error) {
        console.error('Failed to fetch hazards:', error);
      }
    };
    fetchHazards();
  }, []);

  const saveDraft = useCallback(async () => {
    if (!formData.project_name && !formData.work_description) return;
    setIsSaving(true);
    try {
      await offlineDB.setUserData('ptp_draft', { ...formData, updated_at: new Date().toISOString() });
      setLastSaved(new Date().toISOString());
    } catch (error) {
      console.error('Failed to save draft:', error);
    }
    setIsSaving(false);
  }, [formData, offlineDB]);

  // Auto-save every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      void saveDraft();
    }, 30000);
    return () => clearInterval(interval);
  }, [saveDraft]);

  // Load draft on mount
  useEffect(() => {
    const initDraft = async () => {
      try {
        const draft = await offlineDB.getUserData('ptp_draft');
        if (draft) {
          setFormData(draft);
          setLastSaved(draft.updated_at);
          toast({ title: 'Draft Restored', description: 'Your previous draft has been loaded' });
        }
      } catch (error) {
        console.error('Failed to load draft:', error);
      }
    };
    initDraft();
  }, [offlineDB, toast]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleHazard = (hazard) => {
    setFormData(prev => ({
      ...prev,
      selectedHazards: prev.selectedHazards.includes(hazard)
        ? prev.selectedHazards.filter(h => h !== hazard)
        : [...prev.selectedHazards, hazard]
    }));
  };

  const togglePPE = (ppe) => {
    setFormData(prev => ({
      ...prev,
      ppe_required: prev.ppe_required.includes(ppe)
        ? prev.ppe_required.filter(p => p !== ppe)
        : [...prev.ppe_required, ppe]
    }));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    const ptpData = {
      date: formData.date,
      project_name: formData.project_name,
      location: formData.location,
      supervisor: formData.supervisor,
      work_description: formData.work_description,
      equipment_used: formData.equipment_used,
      hazards: formData.selectedHazards,
      additional_hazards: formData.additional_hazards,
      control_measures: formData.control_measures,
      ppe_required: formData.ppe_required,
      status: 'submitted'
    };

    if (isOnline) {
      try {
        const token = localStorage.getItem('auth_token');
        const response = await fetch(`${API_URL}/api/daily-ptp`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(ptpData)
        });

        if (response.ok) {
          await offlineDB.setUserData('ptp_draft', null);
          setShowSuccess(true);
        } else {
          throw new Error('Failed to submit');
        }
      } catch (error) {
        // Save offline if submission fails
        await syncManager.queueForSync('ptp', ptpData, '/api/daily-ptp', 'create');
        await offlineDB.setUserData('ptp_draft', null);
        setShowSuccess(true);
        toast({
          title: 'Saved Offline',
          description: 'PTP will sync when you\'re back online',
          variant: 'warning'
        });
      }
    } else {
      await syncManager.queueForSync('ptp', ptpData, '/api/daily-ptp', 'create');
      await offlineDB.setUserData('ptp_draft', null);
      setShowSuccess(true);
    }
    
    setIsSubmitting(false);
  };

  const nextStep = () => setCurrentStep(prev => Math.min(prev + 1, 4));
  const prevStep = () => setCurrentStep(prev => Math.max(prev - 1, 1));

  const steps = [
    { number: 1, title: 'Basic Info' },
    { number: 2, title: 'Work' },
    { number: 3, title: 'Hazards' },
    { number: 4, title: 'Controls' }
  ];

  if (showSuccess) {
    return (
      <div className="min-h-screen bg-green-50 flex items-center justify-center p-4" data-testid="ptp-success">
        <Card className="w-full max-w-md text-center">
          <CardContent className="pt-8 pb-8">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-green-800 mb-2">PTP Submitted!</h2>
            <p className="text-green-600 mb-6">
              {isOnline ? 'Your Pre-Task Plan has been recorded.' : 'Saved offline. Will sync when online.'}
            </p>
            <div className="space-y-3">
              <TouchButton onClick={() => navigate('/field')} variant="success">
                Back to Field Mode
              </TouchButton>
              <TouchButton onClick={() => {
                setShowSuccess(false);
                setCurrentStep(1);
                setFormData({
                  date: new Date().toISOString().split('T')[0],
                  project_name: '', location: '', supervisor: '',
                  work_description: '', equipment_used: '',
                  selectedHazards: [], additional_hazards: '',
                  control_measures: '', ppe_required: []
                });
              }} variant="outline">
                Create Another PTP
              </TouchButton>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="mobile-ptp-form">
      <MobileFormHeader
        title="Daily PTP"
        subtitle="Pre-Task Planning"
        onBack={() => navigate('/field')}
        icon={ClipboardList}
      />

      <div className="p-4 space-y-4">
        <StepIndicator steps={steps} currentStep={currentStep} />
        <AutoSaveIndicator lastSaved={lastSaved} isSaving={isSaving} />

        {/* Step 1: Basic Info */}
        {currentStep === 1 && (
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <MapPin className="w-5 h-5 text-blue-600" />
                  Project Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-base">Date</Label>
                  <Input
                    type="date"
                    value={formData.date}
                    onChange={(e) => handleChange('date', e.target.value)}
                    className="h-12 text-base"
                  />
                </div>
                <div>
                  <Label className="text-base">Project/Job Name *</Label>
                  <Input
                    value={formData.project_name}
                    onChange={(e) => handleChange('project_name', e.target.value)}
                    placeholder="Enter project name"
                    className="h-12 text-base"
                  />
                </div>
                <div>
                  <Label className="text-base">Location *</Label>
                  <Input
                    value={formData.location}
                    onChange={(e) => handleChange('location', e.target.value)}
                    placeholder="Work location"
                    className="h-12 text-base"
                  />
                </div>
                <div>
                  <Label className="text-base">Supervisor</Label>
                  <Input
                    value={formData.supervisor}
                    onChange={(e) => handleChange('supervisor', e.target.value)}
                    placeholder="Supervisor name"
                    className="h-12 text-base"
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Step 2: Work Description */}
        {currentStep === 2 && (
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <ClipboardList className="w-5 h-5 text-blue-600" />
                  Work Description
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-base">Describe the work to be performed *</Label>
                  <Textarea
                    value={formData.work_description}
                    onChange={(e) => handleChange('work_description', e.target.value)}
                    placeholder="Detailed description of tasks..."
                    className="min-h-[120px] text-base"
                  />
                </div>
                <div>
                  <Label className="text-base">Equipment/Tools Used</Label>
                  <Textarea
                    value={formData.equipment_used}
                    onChange={(e) => handleChange('equipment_used', e.target.value)}
                    placeholder="List equipment and tools..."
                    className="min-h-[80px] text-base"
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Step 3: Hazards */}
        {currentStep === 3 && (
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <AlertTriangle className="w-5 h-5 text-yellow-600" />
                  Identify Hazards
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-2">
                  {hazardCategories.length > 0 ? (
                    hazardCategories.flatMap(cat => cat.hazards || []).slice(0, 12).map((hazard) => (
                      <button
                        key={hazard}
                        onClick={() => toggleHazard(hazard)}
                        className={`p-3 rounded-lg text-left text-sm font-medium transition-all ${
                          formData.selectedHazards.includes(hazard)
                            ? 'bg-yellow-100 border-2 border-yellow-500 text-yellow-800'
                            : 'bg-gray-100 border-2 border-transparent text-gray-700'
                        }`}
                      >
                        {hazard}
                      </button>
                    ))
                  ) : (
                    ['Falls', 'Electrical', 'Chemical', 'Moving Equipment', 'Confined Space', 'Heat/Cold', 'Noise', 'Struck By'].map((hazard) => (
                      <button
                        key={hazard}
                        onClick={() => toggleHazard(hazard)}
                        className={`p-3 rounded-lg text-left text-sm font-medium transition-all ${
                          formData.selectedHazards.includes(hazard)
                            ? 'bg-yellow-100 border-2 border-yellow-500 text-yellow-800'
                            : 'bg-gray-100 border-2 border-transparent text-gray-700'
                        }`}
                      >
                        {hazard}
                      </button>
                    ))
                  )}
                </div>
                <div>
                  <Label className="text-base">Additional Hazards</Label>
                  <Textarea
                    value={formData.additional_hazards}
                    onChange={(e) => handleChange('additional_hazards', e.target.value)}
                    placeholder="Any other hazards not listed..."
                    className="min-h-[60px] text-base"
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Step 4: Controls */}
        {currentStep === 4 && (
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Users className="w-5 h-5 text-green-600" />
                  Control Measures
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-base">How will hazards be controlled? *</Label>
                  <Textarea
                    value={formData.control_measures}
                    onChange={(e) => handleChange('control_measures', e.target.value)}
                    placeholder="Describe safety measures..."
                    className="min-h-[100px] text-base"
                  />
                </div>
                <div>
                  <Label className="text-base mb-3 block">Required PPE</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {ppeOptions.map((ppe) => (
                      <button
                        key={ppe}
                        onClick={() => togglePPE(ppe)}
                        className={`p-3 rounded-lg text-left text-sm font-medium transition-all ${
                          formData.ppe_required.includes(ppe)
                            ? 'bg-green-100 border-2 border-green-500 text-green-800'
                            : 'bg-gray-100 border-2 border-transparent text-gray-700'
                        }`}
                      >
                        {ppe}
                      </button>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Offline Warning */}
            {!isOnline && (
              <Card className="bg-yellow-50 border-yellow-200">
                <CardContent className="py-4">
                  <p className="font-medium text-yellow-800">You&apos;re currently offline</p>
                  <p className="text-sm text-yellow-700">
                    Your PTP will be saved locally and synced when you&apos;re back online.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex gap-3 pt-4 pb-8">
          {currentStep > 1 && (
            <TouchButton onClick={prevStep} variant="outline" className="flex-1">
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back
            </TouchButton>
          )}
          
          {currentStep < 4 ? (
            <TouchButton onClick={nextStep} className="flex-1">
              Continue
              <ArrowRight className="w-5 h-5 ml-2" />
            </TouchButton>
          ) : (
            <TouchButton
              onClick={handleSubmit}
              variant="success"
              className="flex-1"
              disabled={isSubmitting || !formData.project_name || !formData.work_description}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Check className="w-5 h-5 mr-2" />
                  Submit PTP
                </>
              )}
            </TouchButton>
          )}
        </div>
      </div>
    </div>
  );
};

export default MobilePTPForm;
